import { dbDelete, dbGet, dbKeys, dbPut } from "../storage/db.js";
import type { EpgResponse } from "./api.js";

/**
 * Device-side EPG cache (technical-spec §6.2 "client caches"). Guide data is
 * shared, slow-changing, and big — perfect for IndexedDB so the guide renders
 * instantly on next load instead of waiting on the network.
 *
 * Layering rule: the cache is a *placeholder* that never blocks. The app
 * serves cached data immediately, refreshes in the background, and
 * write-throughs the fresh response on every successful EPG upload. User
 * state (Continue Watching, Recently Watched) lives separately in
 * localStorage zustand stores and is always written synchronously — this
 * cache can never delay those.
 *
 * Version stamp: the server's `fetchedAt` (the feed's parse time) is stored
 * with the entry. On every write the newest feed replaces the old one, so the
 * cache always reflects the latest EPG upload. Stale reads are dropped by
 * comparing stamps rather than trusting wall-clock age.
 */

export interface EpgCacheEntry {
  /** Server's feed parse time — the version stamp for this entry. */
  fetchedAt: string;
  /** When this device stored the entry (ISO). */
  savedAt: string;
  /** Full EPG response (programs, channels, nameMapping). */
  data: EpgResponse;
}

/** Cache key: source + window bucket, so 4h/12h/24h windows don't collide. */
export function epgCacheKey(sourceId: string, windowHours: number): string {
  return `${sourceId}|${windowHours}`;
}

/** Read the cached EPG for a source/window. Resolves null on any failure
 * (DB unavailable in private mode, missing entry, corrupt value) — callers
 * treat null as "no cache" and just wait on the network. */
export async function readEpgCache(sourceId: string, windowHours: number): Promise<EpgCacheEntry | null> {
  try {
    const entry = await dbGet<EpgCacheEntry>("epgCache", epgCacheKey(sourceId, windowHours));
    if (!entry || typeof entry !== "object") return null;
    if (typeof entry.fetchedAt !== "string" || !entry.data || !Array.isArray(entry.data.programs)) return null;
    return entry;
  } catch {
    return null;
  }
}

/**
 * Drop every cached guide window for a source. For when the source's guide
 * FEED changes (an M3U source's typed-in EPG URL): the guide cached from the
 * old feed would otherwise keep painting, and the write-through guard below
 * would even refuse a new feed that happens to carry fewer programmes.
 */
export async function forgetEpgCache(sourceId: string): Promise<void> {
  try {
    const prefix = `${sourceId}|`;
    for (const key of await dbKeys("epgCache")) if (key.startsWith(prefix)) await dbDelete("epgCache", key);
  } catch {
    // Non-fatal, like every cache operation here.
  }
}

/**
 * Write-through: store the fresh EPG response, replacing any older feed.
 *
 * Guard: the EPG query fires early with a partial channel list (channels
 * stream in batches), so a sparse response never clobbers a fuller cached
 * one — we only replace when the new response has at least as many programs.
 */
export async function writeEpgCache(sourceId: string, windowHours: number, data: EpgResponse): Promise<void> {
  try {
    const existing = await readEpgCache(sourceId, windowHours);
    if (existing && existing.data.programs.length > data.programs.length) return;
    const entry: EpgCacheEntry = {
      fetchedAt: data.fetchedAt,
      savedAt: new Date().toISOString(),
      data,
    };
    await dbPut("epgCache", entry, epgCacheKey(sourceId, windowHours));
  } catch {
    // Non-fatal: cache is an optimization, never a dependency.
  }
}
